import { Box, Stack, Heading, Text, Button, Alert, IconRefresh } from 'braid-design-system';
import { useTranslation } from 'react-i18next';

interface ErrorBoundaryPageProps {
    error: Error | null;
}

const ErrorBoundaryPage = ({ error }: ErrorBoundaryPageProps) => {
    const { t } = useTranslation();

    return (
        <>
            <Box paddingTop={"large"} display="flex" alignItems="center" justifyContent="center" width={"full"}>
                {/* Main Error Card */}
                <Stack space="medium" align="center">
                    <Stack space="small" align="center">
                        <Text size="large">⚠️</Text>
                        <Heading level="1" align="center">
                            {t('page.error_boundary.title', { defaultValue: 'Oops! Something went wrong' })}
                        </Heading>
                    </Stack>
                    <Stack space="small" align="center">
                        <Text align="center" tone="secondary">
                            {t('page.error_boundary.description', { defaultValue: "We encountered an unexpected error while processing your request. Don't worry, our team has been notified and is working on a fix." })}
                        </Text>
                    </Stack>

                    {/* Error Details */}
                    {error && (

                        <Alert tone="critical">
                            <Stack space="small" align="center">
                                <Text weight="strong" align="center">
                                    {t('page.error_boundary.error_details_label', { defaultValue: 'Error Details:' })}
                                </Text>
                                <Text size="small" align="center">
                                    {error.message}
                                </Text>
                            </Stack>
                        </Alert>
                    )}

                    {/* Action Buttons */}
                    <Stack space="medium" align="center">
                        <Heading level="3" align="center">
                            {t('page.error_boundary.what_can_you_do_heading', { defaultValue: 'What can you do?' })}
                        </Heading>

                        <Stack space="small" align="center">
                            <Text align="center">• {t('page.error_boundary.action.refresh', { defaultValue: 'Try refreshing the page' })}</Text>
                            <Text align="center">• {t('page.error_boundary.action.check_connection', { defaultValue: 'Check your internet connection' })}</Text>
                            <Text align="center">• {t('page.error_boundary.action.contact_support', { defaultValue: 'Contact support if the problem persists' })}</Text>
                        </Stack>

                        <Button onClick={() => window.location.reload()} tone="brandAccent" icon={<IconRefresh />}>
                            {t('page.error_boundary.reload_button', { defaultValue: 'Reload Page' })}
                        </Button>
                    </Stack>

                    {/* Support Info */}
                    <Stack space="small" align="center">
                        <Text weight="strong" align="center">
                            {t('page.error_boundary.need_help', { defaultValue: 'Need Help?' })}
                        </Text>
                        <Text tone="secondary" align="center">
                            {t('page.error_boundary.support_description', { defaultValue: 'If this error continues to occur, please contact our support team with the error details above.' })}
                        </Text>
                    </Stack>
                </Stack>
            </Box>
        </>
    );
};

export default ErrorBoundaryPage;